import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useSelector } from 'react-redux';
import { useDidMount } from 'hooks/useDidMount';
import { createNewPost, getPosts } from 'services/postService';
import LoaderWithText from 'components/@shared/Loaders/LoaderWithText';
import { useLazyLoadList } from 'hooks/useLazyLoadList';
import { PostsResponse } from 'api/post/types';
import { PaginateParamsRequest } from 'types/common';
import { PostFormData } from 'types/post';
import { getUserId } from 'store/session/selectors';
import { AppointmentComment } from './AppointmentComment';
import { initialParams } from './utils';
import { NewCommentForm } from './NewCommentForm';

export const AppointmentComments = () => {
    const userId = useSelector(getUserId);
    const [isLoading, setIsLoading] = useState(true);
    const [listParams, setListParams] = useState<PaginateParamsRequest>(initialParams);
    const [comments, setComments] = useState<PostsResponse>({ items: [], count: 0 });

    const { id } = useParams<{ id: string }>();

    const loadMoreItems = useCallback(() => {
        if (id && comments.count > comments.items.length) {
            setIsLoading(true);
            const updatedParams = { ...listParams, page: listParams.page + 1 };
            getPosts(updatedParams, id)
                .then((list) => {
                    setComments(list);
                    setListParams(updatedParams);
                })
                .finally(() => setIsLoading(false));
        }
    }, [id, comments, listParams]);

    const submitHandler = useCallback(
        (values: PostFormData) => {
            if (id && userId) {
                return createNewPost(values, id, userId).then((comment) => {
                    setIsLoading(true);
                    return getPosts(initialParams, id)
                        .then(setComments)
                        .then(() => comment)
                        .finally(() => setIsLoading(false));
                });
            }
            return Promise.reject();
        },
        [userId, id],
    );

    const targetRef = useLazyLoadList<HTMLDivElement>(loadMoreItems);

    useDidMount(() => {
        if (id) {
            getPosts(listParams, id)
                .then(setComments)
                .finally(() => setIsLoading(false));
        }
    });

    return (
        <Box pt={1}>
            <NewCommentForm onSubmit={submitHandler} />
            <Stack gap={1} py={2}>
                {comments.items.map((comment) => (
                    <AppointmentComment {...comment} />
                ))}
                <div ref={targetRef} />
            </Stack>
            {isLoading && (
                <Box width="100%" height={300} position="relative">
                    <LoaderWithText />
                </Box>
            )}
        </Box>
    );
};
